import React, { useMemo, useState, useEffect } from 'react';
import { Row, Col, Input, DatePicker, Typography, Divider, Spin, Tag, Tooltip, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import type { QaqcPipeInspectionRecord } from '../../../../../types';
import StandardTable from '../../../../../components/common/StandardTable';
import ActionButtons from '../../../../../components/common/ActionButtons';
import { SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { fetchSqlData } from '../../../../../services/api';
import { CgAttachment } from 'react-icons/cg';

const RCgAttachment: React.FC<any> = (props) =>
  React.createElement(CgAttachment as unknown as React.ComponentType<any>, props) as React.ReactElement;

const { Title } = Typography;
const { RangePicker } = DatePicker;

const QaqcPipeInspectionList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Filters
  const [filterReportId, setFilterReportId] = useState('');
  const [filterIssuedBy, setFilterIssuedBy] = useState('');
  const [filterNotes, setFilterNotes] = useState('');
  const [filterAttendees, setFilterAttendees] = useState('');
  const [filterInsType, setFilterInsType] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

  // Data
  const { data, isLoading } = useQuery<QaqcPipeInspectionRecord[], Error>({
    queryKey: ['qaqc_pipe_inspection_records'],
    queryFn: async () => {
      const res = await fetchSqlData('qaqc_pipe_inspection_records');
      // Backend may return { data } or an array
      return (Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []) as QaqcPipeInspectionRecord[];
    },
  });
  const records: QaqcPipeInspectionRecord[] = data || [];

  const insTypeOptions = useMemo(() => {
    const set = new Set<string>();
    (records || []).forEach((r) => {
      const t = String(r.inspection_type || '').trim();
      if (t) set.add(t);
    });
    return Array.from(set).sort();
  }, [records]);

  // Optional: handle event-based refresh pattern similar to RFI list
  useEffect(() => {
    const state: any = location.state;
    if (state?.refreshList) {
      try { window.dispatchEvent(new CustomEvent('qaqc_pipe_inspections:changed')); } catch {}
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const filteredData = useMemo(() => {
    const [start, end] = dateRange || [null, null];
    const fReportId = filterReportId.trim();
    const fIssued = filterIssuedBy.trim();
    const fNotes = filterNotes.trim().toLowerCase();
    const fAtt = filterAttendees.trim().toLowerCase();
    const fType = filterInsType.trim().toLowerCase();
    return (records || []).filter(item => {
      // Report ID & Issued By exact match if selected via Select
      if (fReportId && String(item.report_id || '') !== fReportId) return false;
      if (fIssued && String(item.issued_by || '') !== fIssued) return false;
      if (fNotes) {
        const notesStr = String(item.report_notes || '').toLowerCase();
        const tokens = fNotes.split(/\s+/).filter(Boolean);
        if (tokens.length && !tokens.every(t => notesStr.includes(t))) return false;
      }
      if (fAtt) {
        const arr: string[] = Array.isArray((item as any)?.attendees) ? (item as any).attendees : [];
        const joined = arr.join(', ').toLowerCase();
        const tokens = fAtt.split(/\s+/).filter(Boolean);
        if (tokens.length && !tokens.every(t => joined.includes(t))) return false;
      }
      if (fType && !String(item.inspection_type || '').toLowerCase().includes(fType)) return false;
      // Date range
      if (start || end) {
        const d = item.report_date ? dayjs(item.report_date) : null;
        if (!d) return false;
        if (start && d.isBefore(start, 'day')) return false;
        if (end && d.isAfter(end, 'day')) return false;
      }
      return true;
    });
  }, [records, filterReportId, filterIssuedBy, filterNotes, filterAttendees, filterInsType, dateRange]);

  // Cascading options for Report ID and Issued By
  const reportIdOptions = useMemo(() => {
    const [start, end] = dateRange || [null, null];
    const fIssued = filterIssuedBy.trim();
    const fNotes = filterNotes.trim().toLowerCase();
    const fAtt = filterAttendees.trim().toLowerCase();
    const fType = filterInsType.trim().toLowerCase();
    const pool = (records || []).filter(item => {
      // exclude reportId itself to build options
      if (fIssued && String(item.issued_by || '') !== fIssued) return false;
      if (fNotes) {
        const notesStr = String(item.report_notes || '').toLowerCase();
        const tokens = fNotes.split(/\s+/).filter(Boolean);
        if (tokens.length && !tokens.every(t => notesStr.includes(t))) return false;
      }
      if (fAtt) {
        const arr: string[] = Array.isArray((item as any)?.attendees) ? (item as any).attendees : [];
        const joined = arr.join(', ').toLowerCase();
        const tokens = fAtt.split(/\s+/).filter(Boolean);
        if (tokens.length && !tokens.every(t => joined.includes(t))) return false;
      }
      if (fType && !String(item.inspection_type || '').toLowerCase().includes(fType)) return false;
      if (start || end) {
        const d = item.report_date ? dayjs(item.report_date) : null;
        if (!d) return false;
        if (start && d.isBefore(start, 'day')) return false;
        if (end && d.isAfter(end, 'day')) return false;
      }
      return true;
    });
    const set = new Set<string>();
    pool.forEach(it => { const v = String(it.report_id || '').trim(); if (v) set.add(v); });
    return Array.from(set).sort().map(v => ({ label: v, value: v }));
  }, [records, filterIssuedBy, filterNotes, filterAttendees, filterInsType, dateRange]);

  const issuedByOptions = useMemo(() => {
    const [start, end] = dateRange || [null, null];
    const fReportId = filterReportId.trim();
    const fNotes = filterNotes.trim().toLowerCase();
    const fAtt = filterAttendees.trim().toLowerCase();
    const fType = filterInsType.trim().toLowerCase();
    const pool = (records || []).filter(item => {
      if (fReportId && String(item.report_id || '') !== fReportId) return false;
      if (fNotes) {
        const notesStr = String(item.report_notes || '').toLowerCase();
        const tokens = fNotes.split(/\s+/).filter(Boolean);
        if (tokens.length && !tokens.every(t => notesStr.includes(t))) return false;
      }
      if (fAtt) {
        const arr: string[] = Array.isArray((item as any)?.attendees) ? (item as any).attendees : [];
        const joined = arr.join(', ').toLowerCase();
        const tokens = fAtt.split(/\s+/).filter(Boolean);
        if (tokens.length && !tokens.every(t => joined.includes(t))) return false;
      }
      if (fType && !String(item.inspection_type || '').toLowerCase().includes(fType)) return false;
      if (start || end) {
        const d = item.report_date ? dayjs(item.report_date) : null;
        if (!d) return false;
        if (start && d.isBefore(start, 'day')) return false;
        if (end && d.isAfter(end, 'day')) return false;
      }
      return true;
    });
    const set = new Set<string>();
    pool.forEach(it => { const v = String(it.issued_by || '').trim(); if (v) set.add(v); });
    return Array.from(set).sort().map(v => ({ label: v, value: v }));
  }, [records, filterReportId, filterNotes, filterAttendees, filterInsType, dateRange]);

  const columns: ColumnsType<QaqcPipeInspectionRecord> = useMemo(() => {
    const cols: any[] = [
      { title: 'Report ID', dataIndex: 'report_id', key: 'report_id', width: 145 },
      { title: 'Issued By', dataIndex: 'issued_by', key: 'issued_by', width: 160 },
      {
        title: '', key: 'has_files', width: 40, align: 'center' as const,
        render: (_: any, row: QaqcPipeInspectionRecord) => {
          const has = !!row.upload_path && String(row.upload_path).trim() !== '';
          return has ? (
            <Tooltip title="Has attachments">
              <RCgAttachment size={16} color="#1677ff" />
            </Tooltip>
          ) : null;
        },
      },
      { title: 'Notes', dataIndex: 'report_notes', key: 'report_notes', ellipsis: true },
      { title: 'Attendees', key: 'attendees', width: 220, render: (_: any, row: QaqcPipeInspectionRecord) => {
        const arr: string[] = Array.isArray((row as any)?.attendees) ? (row as any).attendees : [];
        return arr.join(', ');
      }},
      { title: 'Report Date', dataIndex: 'report_date', key: 'report_date', width: 110, render: (v: string) => v ? dayjs(v).format('YYYY-MM-DD') : '' },
      { title: 'InsType', dataIndex: 'inspection_type', key: 'inspection_type', width: 80 },
      { title: 'Weld#', key: 'weld_count', width: 70, render: (_: any, row: QaqcPipeInspectionRecord) => Array.isArray(row.weldingdata_ids) ? row.weldingdata_ids.length : 0 },
      { title: 'P', key: 'res_p', width: 40, align: 'center', render: (_: any, row: QaqcPipeInspectionRecord) => {
        const arr = Array.isArray((row as any)?.results) ? (row as any).results : [];
        const p = arr.reduce((n: number, v: any) => n + (String(v).toUpperCase() === 'P' ? 1 : 0), 0);
        return p;
      }},
      { title: 'F', key: 'res_f', width: 40, align: 'center', render: (_: any, row: QaqcPipeInspectionRecord) => {
        const arr = Array.isArray((row as any)?.results) ? (row as any).results : [];
        const f = arr.reduce((n: number, v: any) => n + (String(v).toUpperCase() === 'F' ? 1 : 0), 0);
        return f;
      }},
      { title: 'R', key: 'res_r', width: 40, align: 'center', render: (_: any, row: QaqcPipeInspectionRecord) => {
        const arr = Array.isArray((row as any)?.results) ? (row as any).results : [];
        const r = arr.reduce((n: number, v: any) => n + (String(v).toUpperCase() === 'R' ? 1 : 0), 0);
        return r;
      }},
      { title: 'Status', dataIndex: 'report_status', key: 'report_status', width: 120, render: (s?: string) => {
        const color = s === 'active' ? 'green' : s === 'archived' ? 'default' : s === 'cancelled' ? 'red' : 'gold';
        return <Tag color={color}>{s || 'draft'}</Tag>;
      } },
      {
        title: 'Action', key: 'action', width: 120, fixed: 'right',
        render: (_: any, row: QaqcPipeInspectionRecord) => (
          <ActionButtons
            onView={() => navigate(`/qaqc/ndt/pipe_inspections/inspection/view/${row.report_id}`)}
            onEdit={() => navigate(`/qaqc/ndt/pipe_inspections/inspection/edit/${row.report_id}`)}
            showView={true}
            showEdit={true}
            showDelete={false}
            size="small"
          />
        )
      },
    ];
    return cols;
  }, [navigate]);

  return (
    <>
      <Divider style={{ marginTop: 0 }} />
      <div style={{ padding: '0 24px' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>Pipe Inspections</Title>
          </Col>
          <Col>
            {/* No generic create button; inspections are created from an RFI context */}
          </Col>
        </Row>

        {/* Filters */}
        <Row gutter={[4, 8]} style={{ marginBottom: 16}}>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Select
              allowClear
              placeholder="Report ID"
              value={filterReportId || undefined}
              onChange={(v) => setFilterReportId((v as string) || '')}
              options={reportIdOptions}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Select
              allowClear
              placeholder="Issued By"
              value={filterIssuedBy || undefined}
              onChange={(v) => setFilterIssuedBy((v as string) || '')}
              options={issuedByOptions}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
            />
          </Col>
          
          <Col xs={24} sm={12} md={8} lg={4}>
            <Input
              placeholder="Notes"
              value={filterNotes}
              onChange={(e) => setFilterNotes(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Input
              placeholder="Attendees"
              value={filterAttendees}
              onChange={(e) => setFilterAttendees(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={2}>
            <Select
              allowClear
              placeholder="Type"
              value={filterInsType || undefined}
              onChange={(v) => setFilterInsType((v as string) || '')}
              options={insTypeOptions.map((v) => ({ label: v, value: v }))}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} sm={24} md={8} lg={5}>
            <RangePicker
              style={{ width: '90%'}}
              value={dateRange as any}
              onChange={(vals) => setDateRange(vals as any)}
              placeholder={["Start date", "End date"]}
            />
          </Col>
        </Row>

        <Spin spinning={isLoading}>
          <StandardTable
            columns={columns as any}
            dataSource={filteredData}
            rowKey={(r: QaqcPipeInspectionRecord) => String(r.report_id)}
            loading={isLoading}
            pagination={{ pageSize: 15 }}
            scroll={{ x: 'max-content' }}
            bordered
            size="small"
          />
        </Spin>
      </div>
    </>
  );
};

export default QaqcPipeInspectionList;
